"use client";

import { useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  AppWindow,
  Bot,
  Boxes,
  BriefcaseBusiness,
  Cable,
  ChevronRight,
  Clock3,
  Database,
  FileText,
  FolderOpen,
  Gauge,
  History,
  LayoutGrid,
  Menu,
  MoreVertical,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  UserRound,
  Workflow,
  Wrench,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AiWorkspace as CoreAiWorkspace } from "./ai-workspace-core";
import type { WorkspaceMode } from "./ai-workspace-types";
import { UniversalAiModelPicker } from "./universal-ai-model-picker";
import { EnterpriseContextMenu } from "./enterprise-context-menu";
import { RunyiAutomationManager } from "./runyi-automation-manager";

type AiMenuAction =
  | "chat"
  | "work"
  | "new"
  | "history"
  | "infrastructure"
  | "applications"
  | "operations"
  | "bridge"
  | "governance"
  | "files"
  | "automations"
  | "none";

type AiMenuItem = {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  action: AiMenuAction;
  keywords: string[];
};

type AiMenuGroup = {
  title: string;
  items: AiMenuItem[];
};

type AiAgentRun = {
  session_id?: string;
  provider_key?: string;
  model_key?: string;
  status?: string;
  model_calls?: number;
  tool_calls?: number;
  created_at?: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    reasoning_tokens?: number;
    estimated_cost_usd?: number;
  };
  task?: {
    goal?: string;
    intent?: string;
    operation_key?: string;
    result_status?: string;
    lifecycle_state?: string;
  };
};

type AiUsageRecord = {
  contract?: string;
  active_agents?: number;
  summary?: {
    today_cost_usd?: number;
    month_cost_usd?: number;
    inference_calls?: number;
    total_tokens?: number;
  };
  agent_runs?: AiAgentRun[];
  provider_health?: Array<{ provider_key?: string; health_state?: string; status?: string }>;
};

type AgentTaskDetailTab = "overview" | "instructions" | "tools" | "context" | "state" | "usage";

type AiAgentTaskDetail = {
  contract?: string;
  session_id?: string;
  historical_snapshot?: boolean;
  regenerated?: boolean;
  overview?: Record<string, any>;
  instructions?: { text?: string; source?: string; immutable?: boolean; regenerated?: boolean; assembler_contract?: string };
  tools?: { contracts?: Array<Record<string, any>>; contract_source?: string; allowed_operations?: string[]; calls?: Array<Record<string, any>> };
  knowledge_context?: Record<string, any>;
  state?: Record<string, any>;
  usage?: { session_totals?: Record<string, any>; inference_calls?: Array<Record<string, any>>; compaction?: Record<string, any> };
  secret_values_exposed?: boolean;
};

const aiMenuGroups: AiMenuGroup[] = [
  {
    title: "Workspace",
    items: [
      { key: "chat", label: "Chat", description: "Open the existing RUNYI Chat workspace.", icon: Bot, action: "chat", keywords: ["conversation", "assistant"] },
      { key: "work", label: "Work", description: "Open the existing agent-style RUNYI Work workspace.", icon: BriefcaseBusiness, action: "work", keywords: ["agent", "task", "browser"] },
      { key: "new", label: "New Session", description: "Start a clean AI conversation without changing the workspace design.", icon: Plus, action: "new", keywords: ["new", "conversation", "session"] },
      { key: "history", label: "Recent Sessions", description: "Open the existing conversation history.", icon: History, action: "history", keywords: ["recent", "chat history", "work history"] },
    ],
  },
  {
    title: "System Intelligence",
    items: [
      { key: "system", label: "RUNYI System Knowledge", description: "Use the AI workspace with system-wide RUNYI context permitted to your account.", icon: Sparkles, action: "chat", keywords: ["system", "knowledge", "architecture"] },
      { key: "infra", label: "Infrastructure Intelligence", description: "Open infrastructure truth, capacity, usage and availability.", icon: Database, action: "infrastructure", keywords: ["infrastructure", "capacity", "usage", "provider"] },
      { key: "apps", label: "Application Intelligence", description: "Open applications and their infrastructure consumption.", icon: AppWindow, action: "applications", keywords: ["applications", "environment", "resource usage"] },
      { key: "ops", label: "Operations Intelligence", description: "Open provisioning, deployments, reconciliation and incidents.", icon: Workflow, action: "operations", keywords: ["operations", "deployment", "provisioning", "incident"] },
    ],
  },
  {
    title: "Data & Files",
    items: [
      { key: "files", label: "Files", description: "Open the Universal Enterprise File Explorer.", icon: FolderOpen, action: "files", keywords: ["upload", "attachment", "document", "storage", "explorer"] },
      { key: "database", label: "Database & Infrastructure", description: "Open verified infrastructure inventory and database resources.", icon: Database, action: "infrastructure", keywords: ["database", "sql", "postgres", "explorer"] },
      { key: "connections", label: "Connections", description: "Open provider accounts, projects, APIs and bindings.", icon: Cable, action: "bridge", keywords: ["provider", "oauth", "api", "connection"] },
      { key: "documents", label: "Retrieved Documents", description: "Return to Chat and work with files already available to the AI session.", icon: FileText, action: "chat", keywords: ["retrieved", "documents", "files"] },
    ],
  },
  {
    title: "Actions",
    items: [
      { key: "automations", label: "Automations", description: "Manage routines, schedules, triggers, webhooks, events and condition watches created in RUNYI.", icon: Boxes, action: "automations", keywords: ["automation", "routine", "schedule", "trigger", "webhook", "event", "watch"] },
      { key: "workflows", label: "Workflows", description: "Open enterprise operations and execution flows.", icon: Workflow, action: "operations", keywords: ["workflow", "flow", "execution"] },
      { key: "tools", label: "Tools", description: "Use the existing RUNYI Work tool environment.", icon: Wrench, action: "work", keywords: ["tools", "browser", "github", "cloudflare"] },
      { key: "provider-actions", label: "Provider Actions", description: "Open provider connections before governed provider operations.", icon: Cable, action: "bridge", keywords: ["provider", "actions", "cloudflare", "supabase"] },
    ],
  },
  {
    title: "History & Audit",
    items: [
      { key: "chat-history", label: "Chat & Work History", description: "Open the preserved conversation history surface.", icon: Clock3, action: "history", keywords: ["history", "sessions", "messages"] },
      { key: "audit", label: "AI Actions & Audit", description: "Open Enterprise governance and audit records.", icon: ShieldCheck, action: "governance", keywords: ["audit", "actions", "changes"] },
    ],
  },
  {
    title: "More",
    items: [
      { key: "permissions", label: "Permissions", description: "Open account authority and delegated access settings.", icon: UserRound, action: "governance", keywords: ["permissions", "roles", "accounts"] },
      { key: "ai-settings", label: "AI & Workspace Settings", description: "Open RUNYI settings while preserving the active AI workspace.", icon: Settings2, action: "governance", keywords: ["settings", "models", "workspace"] },
      { key: "all-functions", label: "RUNYI Functions", description: "Search the complete RUNYI enterprise function directory.", icon: LayoutGrid, action: "none", keywords: ["functions", "directory", "more"] },
    ],
  },
];

function findWorkspaceButton(root: HTMLDivElement | null, label: string) {
  if (!root) return null;
  return Array.from(root.querySelectorAll<HTMLButtonElement>("button")).find(
    (button) => button.textContent?.trim() === label,
  ) ?? null;
}

function navigateRunyi(route: string) {
  const current = window.history.state ?? {};
  const depth = typeof current.runyiDepth === "number" ? current.runyiDepth : 0;
  window.history.replaceState({ ...current, runyiScrollY: window.scrollY }, "", window.location.href);
  const next = { runyiRoute: route, runyiDepth: depth + 1, runyiScrollY: 0 };
  window.history.pushState(next, "", window.location.href);
  window.dispatchEvent(new PopStateEvent("popstate", { state: next }));
}

export function AiWorkspace({
  supabase,
  connected,
  isAdmin,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<WorkspaceMode>("chat");
  const [aiSelection, setAiSelection] = useState<{ provider_key: string; model_key: string }>({ provider_key: "", model_key: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<AiUsageRecord | null>(null);
  const [selectedAgentRun, setSelectedAgentRun] = useState<AiAgentRun | null>(null);
  const [taskDetail, setTaskDetail] = useState<AiAgentTaskDetail | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  const [taskDetailError, setTaskDetailError] = useState<string | null>(null);
  const [taskDetailTab, setTaskDetailTab] = useState<AgentTaskDetailTab>("overview");

  const openUsage = async () => {
    setUsageOpen(true);
    setMenuOpen(false);
    setMoreOpen(false);
    if (!supabase || !connected) {
      setUsageError("RUNYI AI usage is unavailable while disconnected.");
      return;
    }
    setUsageLoading(true);
    setUsageError(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: { screen: "AI_USAGE", mode: "read" },
      });
      if (error) throw error;
      setUsageData((data ?? null) as AiUsageRecord | null);
    } catch (error) {
      setUsageError(error instanceof Error ? error.message : "RUNYI AI usage is temporarily unavailable.");
    } finally {
      setUsageLoading(false);
    }
  };

  const openAgentTask = async (run: AiAgentRun) => {
    if (!run.session_id) return;
    setSelectedAgentRun(run);
    setTaskDetail(null);
    setTaskDetailError(null);
    setTaskDetailTab("overview");
    if (!supabase || !connected) {
      setTaskDetailError("RUNYI Agent Task Details are unavailable while disconnected.");
      return;
    }
    setTaskDetailLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: { screen: "AI_USAGE", mode: "task_detail", session_id: run.session_id },
      });
      if (error) throw error;
      setTaskDetail((data ?? null) as AiAgentTaskDetail | null);
    } catch (error) {
      setTaskDetailError(error instanceof Error ? error.message : "RUNYI Agent Task Details are temporarily unavailable.");
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const closeAgentTask = () => {
    setSelectedAgentRun(null);
    setTaskDetail(null);
    setTaskDetailError(null);
    setTaskDetailTab("overview");
  };

  const filteredGroups = useMemo(() => {
    const needle = menuQuery.trim().toLowerCase();
    if (!needle) return aiMenuGroups;
    return aiMenuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          [group.title, item.label, item.description, ...item.keywords]
            .join(" ")
            .toLowerCase()
            .includes(needle),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [menuQuery]);

  const closeMenu = () => {
    setMenuOpen(false);
    setMenuQuery("");
  };

  const runAction = (action: AiMenuAction) => {
    if (action === "chat" || action === "work") {
      findWorkspaceButton(rootRef.current, action === "chat" ? "Chat" : "Work")?.click();
      closeMenu();
      return;
    }
    if (action === "new") {
      rootRef.current?.querySelector<HTMLButtonElement>('button[aria-label="New conversation"]')?.click();
      closeMenu();
      return;
    }
    if (action === "history") {
      rootRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Open conversation history"]')?.click();
      closeMenu();
      return;
    }
    if (action === "automations") {
      setAutomationOpen(true);
      setUsageOpen(false);
      setMoreOpen(false);
      closeMenu();
      return;
    }
    if (action === "files") {
      navigateRunyi("files");
      closeMenu();
      return;
    }
    if (action === "none") {
      navigateRunyi("more");
      closeMenu();
      return;
    }
    navigateRunyi(action);
    closeMenu();
  };

  return (
    <div ref={rootRef} className="relative flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <div className="relative z-40 flex h-11 shrink-0 items-center gap-1.5 border-b border-foreground/8 bg-background/95 px-2 backdrop-blur-xl sm:px-3">
        <button
          type="button"
          onClick={() => { setMenuOpen(true); setMoreOpen(false); }}
          className="flex min-w-0 items-center gap-2 rounded-xl border border-foreground/8 bg-foreground/[0.025] px-2.5 py-1.5 text-left transition hover:bg-foreground/[0.06]"
          aria-label="Open AI workspace menu"
        >
          <Menu className="size-4 shrink-0 text-primary" />
          <span className="truncate text-[11px] font-semibold">RUNYI AI</span>
          <ChevronRight className="size-3 rotate-90 text-foreground/30" />
        </button>

        <div className="hidden min-w-0 flex-1 items-center gap-2 px-1 sm:flex">
          <span className="truncate text-[10px] text-foreground/38">{connected ? "Live enterprise context" : "Offline context"}</span>
          <span className="size-1 rounded-full bg-foreground/20" />
          <span className="truncate text-[10px] text-foreground/38">{isAdmin ? "Admin authority" : "Member authority"}</span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          <UniversalAiModelPicker supabase={supabase} connected={connected} mode={mode}
            selectedProviderKey={aiSelection.provider_key} selectedModelKey={aiSelection.model_key}
            onSelect={(provider_key, model_key) => setAiSelection({ provider_key, model_key })}
            toolbarCompact />
          <button
            type="button"
            onClick={() => { setMenuOpen(true); setMoreOpen(false); }}
            aria-label="Search AI workspace menu"
            className="grid size-8 place-items-center rounded-lg text-foreground/55 transition hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <Search className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => void openUsage()}
            aria-label="Open AI usage and agent records"
            title="AI usage and agents"
            className="grid size-8 place-items-center rounded-lg text-foreground/55 transition hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <Gauge className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => runAction("new")}
            aria-label="New AI session"
            className="grid size-8 place-items-center rounded-lg text-foreground/55 transition hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <Plus className="size-4" />
          </button>
          <EnterpriseContextMenu compact label="AI workspace actions" title="AI workspace" width={224}
            controlledOpen={moreOpen} onOpenChange={setMoreOpen}>
            <button type="button" onClick={() => { setMoreOpen(false); runAction("history"); }} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs hover:bg-foreground/[0.06]"><History className="size-4 text-foreground/45" />Recent sessions</button>
            <button type="button" onClick={() => { setMoreOpen(false); runAction("none"); }} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs hover:bg-foreground/[0.06]"><LayoutGrid className="size-4 text-foreground/45" />All RUNYI functions</button>
          </EnterpriseContextMenu>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <CoreAiWorkspace supabase={supabase} connected={connected} isAdmin={isAdmin}
          mode={mode} setMode={setMode} aiSelection={aiSelection} setAiSelection={setAiSelection} />
      </div>

      {automationOpen ? (
        <RunyiAutomationManager supabase={supabase} connected={connected} isAdmin={isAdmin} onClose={() => setAutomationOpen(false)} />
      ) : null}

      {usageOpen && (
        <div className="absolute inset-0 z-[95] overflow-hidden">
          <button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" aria-label="Close AI usage" onClick={() => setUsageOpen(false)} />
          <aside className="absolute inset-y-0 right-0 flex h-full max-h-full min-h-0 w-full max-w-[440px] flex-col overflow-hidden border-l border-foreground/10 bg-background shadow-2xl shadow-black/35">
            <div className="flex items-center gap-2 border-b border-foreground/8 p-3">
              <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary"><Gauge className="size-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold">AI Usage & Agents</p>
                <p className="truncate text-[9px] text-foreground/38">Tasks · agents · models · tokens · cost</p>
              </div>
              <Button type="button" variant="ghost" size="icon" className="size-8 rounded-lg" onClick={() => setUsageOpen(false)}><X className="size-4" /></Button>
            </div>

            <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain p-3">
              {usageLoading && !usageData ? (
                <div className="grid min-h-40 place-items-center text-xs text-foreground/45">Loading authoritative AI records…</div>
              ) : null}

              {usageError ? (
                <div className="rounded-xl border border-destructive/20 bg-destructive/[0.06] p-3 text-xs text-destructive">{usageError}</div>
              ) : null}

              {usageData ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <UsageStat label="Today" value={formatAiCost(usageData.summary?.today_cost_usd)} />
                    <UsageStat label="Month" value={formatAiCost(usageData.summary?.month_cost_usd)} />
                    <UsageStat label="API calls" value={formatAiCount(usageData.summary?.inference_calls)} />
                    <UsageStat label="Tokens" value={formatAiCount(usageData.summary?.total_tokens)} />
                    <UsageStat label="Active agents" value={formatAiCount(usageData.active_agents)} />
                    <UsageStat label="Provider" value={usageData.provider_health?.[0]?.health_state ?? "—"} />
                  </div>

                  <section>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-primary">Agent tasks</p>
                        <p className="mt-0.5 text-[9px] text-foreground/38">What AI was used for and what it cost.</p>
                      </div>
                      <span className="rounded-full bg-foreground/[0.05] px-2 py-1 text-[9px] text-foreground/50">{usageData.contract ?? "RUNYI_AI_USAGE_UI_V2"}</span>
                    </div>

                    <div className="space-y-2">
                      {(usageData.agent_runs ?? []).length === 0 ? (
                        <div className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-4 text-center text-xs text-foreground/40">No agent tasks recorded yet.</div>
                      ) : (usageData.agent_runs ?? []).map((run) => (
                        <button
                          key={run.session_id ?? `${run.created_at}-${run.model_key}`}
                          type="button"
                          disabled={!run.session_id}
                          onClick={() => void openAgentTask(run)}
                          className="w-full rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-3 text-left transition hover:border-primary/20 hover:bg-foreground/[0.04] disabled:cursor-default disabled:opacity-70"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold leading-5">{run.task?.goal ?? "RUNYI AI agent task"}</p>
                              {run.task?.intent ? <p className="mt-1 text-[9px] leading-4 text-foreground/42">{run.task.intent}</p> : null}
                            </div>
                            <span className="shrink-0 rounded-full bg-primary/[0.08] px-2 py-1 text-[9px] font-semibold text-primary">{run.task?.result_status ?? run.status ?? "UNKNOWN"}</span>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[9px] sm:grid-cols-3">
                            <UsageDetail label="Operation" value={run.task?.operation_key ?? "—"} />
                            <UsageDetail label="Model" value={run.model_key ?? "—"} />
                            <UsageDetail label="Lifecycle" value={run.task?.lifecycle_state ?? run.status ?? "—"} />
                            <UsageDetail label="Model calls" value={formatAiCount(run.model_calls)} />
                            <UsageDetail label="Tool calls" value={formatAiCount(run.tool_calls)} />
                            <UsageDetail label="Cost" value={formatAiCost(run.usage?.estimated_cost_usd)} />
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2 text-[9px] text-foreground/38">
                            <span>{formatAiCount((run.usage?.input_tokens ?? 0) + (run.usage?.output_tokens ?? 0) + (run.usage?.reasoning_tokens ?? 0))} tokens · {run.provider_key ?? "AI provider"}</span>
                            <span className="flex items-center gap-1 font-medium text-primary/70">Details <ChevronRight className="size-3" /></span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              ) : null}
            </div>
            {selectedAgentRun ? (
              <AgentTaskDetailPanel
                run={selectedAgentRun}
                detail={taskDetail}
                loading={taskDetailLoading}
                error={taskDetailError}
                tab={taskDetailTab}
                onTab={setTaskDetailTab}
                onBack={closeAgentTask}
                onClose={() => { closeAgentTask(); setUsageOpen(false); }}
              />
            ) : null}
          </aside>
        </div>
      )}

      {menuOpen && (
        <div className="absolute inset-0 z-[90]">
          <button type="button" className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" aria-label="Close AI workspace menu" onClick={closeMenu} />
          <aside className="absolute inset-y-0 left-0 flex w-[88%] max-w-[360px] flex-col border-r border-foreground/10 bg-background shadow-2xl shadow-black/35">
            <div className="border-b border-foreground/8 p-3">
              <div className="flex items-center gap-2">
                <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary"><Sparkles className="size-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold">RUNYI AI</p>
                  <p className="truncate text-[9px] text-foreground/38">Workspace menu · existing AI workspace preserved</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="size-8 rounded-lg" onClick={closeMenu}><X className="size-4" /></Button>
              </div>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" />
                <Input autoFocus value={menuQuery} onChange={(event) => setMenuQuery(event.target.value)} placeholder="Search AI functions, files, infrastructure, applications…" className="h-10 rounded-xl border-foreground/10 bg-foreground/[0.025] pl-9 text-xs" />
              </div>
            </div>

            <div className="border-b border-foreground/8 px-3 py-2.5">
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-primary">Context</p>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <ContextChip label="Connection" value={connected ? "Live" : "Offline"} />
                <ContextChip label="Authority" value={isAdmin ? "Admin" : "Member"} />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
              {filteredGroups.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-foreground/38">No AI workspace function matches this search.</p>
              ) : filteredGroups.map((group) => (
                <section key={group.title} className="mb-4 last:mb-1">
                  <p className="px-2 pb-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-primary">{group.title}</p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <button key={item.key} type="button" onClick={() => runAction(item.action)} className="group flex w-full items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition hover:bg-foreground/[0.055]">
                        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-foreground/[0.045] text-foreground/65 group-hover:text-primary"><item.icon className="size-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold">{item.label}</span>
                          <span className="mt-0.5 block text-[9px] leading-4 text-foreground/38">{item.description}</span>
                        </span>
                        <ChevronRight className="mt-2.5 size-3.5 shrink-0 text-foreground/20" />
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function AgentTaskDetailPanel({
  run,
  detail,
  loading,
  error,
  tab,
  onTab,
  onBack,
  onClose,
}: {
  run: AiAgentRun;
  detail: AiAgentTaskDetail | null;
  loading: boolean;
  error: string | null;
  tab: AgentTaskDetailTab;
  onTab: (tab: AgentTaskDetailTab) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const tabs: Array<{ key: AgentTaskDetailTab; label: string }> = [
    { key: "overview", label: "Overview" },
    { key: "instructions", label: "Instructions" },
    { key: "tools", label: "Tools" },
    { key: "context", label: "Context" },
    { key: "state", label: "State" },
    { key: "usage", label: "Usage" },
  ];
  const overview = detail?.overview ?? {};
  const usage = detail?.usage ?? {};
  const totals = usage.session_totals ?? {};
  const compaction = usage.compaction ?? {};

  return (
    <div className="absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden bg-background">
      <div className="flex items-center gap-2 border-b border-foreground/8 p-3">
        <button type="button" onClick={onBack} aria-label="Back to AI agent tasks" className="grid size-8 place-items-center rounded-lg text-foreground/55 hover:bg-foreground/[0.06] hover:text-foreground">
          <ChevronRight className="size-4 rotate-180" />
        </button>
        <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold">Agent Task Details</p>
          <p className="truncate text-[9px] text-foreground/38">Historical execution snapshot · not regenerated</p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="size-8 rounded-lg" onClick={onClose}><X className="size-4" /></Button>
      </div>

      <div className="border-b border-foreground/8 p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 text-xs font-semibold leading-5">{overview.goal ?? run.task?.goal ?? "RUNYI AI agent task"}</p>
          <span className="shrink-0 rounded-full bg-primary/[0.08] px-2 py-1 text-[9px] font-semibold text-primary">{overview.status ?? run.status ?? "UNKNOWN"}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-foreground/[0.05] px-2 py-1 text-[8px] text-foreground/55">{overview.provider_key ?? run.provider_key ?? "AI"}</span>
          <span className="rounded-full bg-foreground/[0.05] px-2 py-1 text-[8px] text-foreground/55">{overview.model_key ?? run.model_key ?? "model"}</span>
          <span className="rounded-full bg-foreground/[0.05] px-2 py-1 text-[8px] text-foreground/55">{detail?.contract ?? "RUNYI_AI_AGENT_TASK_DETAIL_V1"}</span>
        </div>
      </div>

      <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-foreground/8 px-2 py-2">
        {tabs.map((item) => (
          <button key={item.key} type="button" onClick={() => onTab(item.key)} className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[9px] font-semibold transition ${tab === item.key ? "bg-primary/10 text-primary" : "text-foreground/45 hover:bg-foreground/[0.05] hover:text-foreground"}`}>
            {item.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain p-3">
        {loading ? <div className="grid min-h-40 place-items-center text-xs text-foreground/45">Loading historical RUNYI execution snapshot…</div> : null}
        {error ? <div className="rounded-xl border border-destructive/20 bg-destructive/[0.06] p-3 text-xs text-destructive">{error}</div> : null}
        {!loading && !error && detail ? (
          <div className="space-y-3">
            {tab === "overview" ? (
              <>
                <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-3 text-[9px] leading-4 text-foreground/65">
                  This view reads the execution snapshot RUNYI stored for this session. It does not run the Context Assembler again.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <UsageDetail label="Profile" value={String(overview.agent_profile_key ?? "—")} />
                  <UsageDetail label="Runtime" value={String(overview.runtime_key ?? "—")} />
                  <UsageDetail label="Model calls" value={formatAiCount(overview.model_calls)} />
                  <UsageDetail label="Tool calls" value={formatAiCount(overview.tool_calls)} />
                  <UsageDetail label="Started" value={formatAuditTime(overview.started_at)} />
                  <UsageDetail label="Completed" value={formatAuditTime(overview.completed_at)} />
                </div>
                <AuditSection title="Task contract" value={detail.state?.task_contract} />
                <AuditSection title="Completion gate" value={detail.state?.completion_gate} />
              </>
            ) : null}

            {tab === "instructions" ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5 text-[8px] text-foreground/45">
                  <span className="rounded-full bg-primary/[0.08] px-2 py-1 text-primary">Exact historical snapshot</span>
                  <span>{detail.instructions?.source ?? "Historical context"}</span>
                  <span>· regenerated: {detail.instructions?.regenerated ? "yes" : "no"}</span>
                </div>
                <pre className="whitespace-pre-wrap break-words rounded-xl border border-foreground/8 bg-foreground/[0.025] p-3 font-mono text-[9px] leading-4 text-foreground/75">{detail.instructions?.text || "No historical assembled instruction snapshot is available for this session."}</pre>
              </>
            ) : null}

            {tab === "tools" ? (
              <>
                <div className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                  <p className="text-[8px] uppercase tracking-wider text-foreground/35">Tool contract source</p>
                  <p className="mt-1 text-[9px] font-medium text-foreground/70">{detail.tools?.contract_source ?? "—"}</p>
                  <div className="mt-2 flex flex-wrap gap-1">{(detail.tools?.allowed_operations ?? []).map((operation) => <span key={operation} className="rounded-full bg-primary/[0.08] px-2 py-1 text-[8px] text-primary">{operation}</span>)}</div>
                </div>
                {(detail.tools?.contracts ?? []).map((tool, index) => (
                  <div key={`${tool.tool_key ?? "tool"}-${index}`} className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                    <p className="text-[10px] font-semibold">{tool.display_name ?? tool.tool_key ?? "RUNYI tool"}</p>
                    <p className="mt-1 text-[9px] leading-4 text-foreground/45">{tool.description ?? ""}</p>
                    <AuditSection title="Input contract" value={tool.input_schema} compact />
                  </div>
                ))}
                {(detail.tools?.calls ?? []).map((call, index) => (
                  <div key={`${call.tool_call_id ?? "call"}-${index}`} className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                    <div className="flex items-center justify-between gap-2"><p className="text-[10px] font-semibold">{call.operation_key ?? call.tool_key ?? "Tool call"}</p><span className="rounded-full bg-primary/[0.08] px-2 py-1 text-[8px] font-semibold text-primary">{call.status ?? "UNKNOWN"}</span></div>
                    <p className="mt-1 text-[9px] leading-4 text-foreground/45">{call.intent ?? ""}</p>
                    <AuditSection title="Arguments" value={call.arguments} compact />
                    <AuditSection title="RUNYI result" value={call.result} compact />
                  </div>
                ))}
              </>
            ) : null}

            {tab === "context" ? <AuditSection title="Knowledge & context" value={detail.knowledge_context} /> : null}

            {tab === "state" ? (
              <>
                <AuditSection title="Execution lease" value={detail.state?.execution_lease} />
                <AuditSection title="Budgets" value={detail.state?.budgets} />
                <AuditSection title="Final result" value={detail.state?.result} />
                <AuditSection title="Resume state" value={detail.state?.resume} />
                <AuditSection title="Session events" value={detail.state?.events} />
              </>
            ) : null}

            {tab === "usage" ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <UsageStat label="Input" value={formatAiCount(totals.input_tokens)} />
                  <UsageStat label="Cached" value={formatAiCount(totals.cached_input_tokens)} />
                  <UsageStat label="Output" value={formatAiCount(totals.output_tokens)} />
                  <UsageStat label="Reasoning" value={formatAiCount(totals.reasoning_tokens)} />
                  <UsageStat label="Total cost" value={formatAiCost(totals.estimated_cost_usd)} />
                  <UsageStat label="Model calls" value={formatAiCount(overview.model_calls)} />
                </div>
                {compaction.contract ? (
                  <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-3">
                    <p className="text-[9px] font-semibold text-primary">{String(compaction.contract)}</p>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[9px]">
                      <UsageDetail label="Source chars" value={formatAiCount(compaction.source_chars)} />
                      <UsageDetail label="Compacted chars" value={formatAiCount(compaction.compacted_chars)} />
                      <UsageDetail label="Saved chars" value={formatAiCount(compaction.saved_chars)} />
                      <UsageDetail label="Ratio" value={compaction.compaction_ratio == null ? "—" : String(compaction.compaction_ratio)} />
                    </div>
                  </div>
                ) : null}
                <AuditSection title="Inference calls" value={usage.inference_calls} />
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AuditSection({ title, value, compact = false }: { title: string; value: unknown; compact?: boolean }) {
  return (
    <details className="mt-2 rounded-xl border border-foreground/8 bg-foreground/[0.02]" open={!compact}>
      <summary className="cursor-pointer px-3 py-2 text-[9px] font-semibold text-foreground/65">{title}</summary>
      <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-words border-t border-foreground/8 p-3 font-mono text-[8px] leading-4 text-foreground/65">{JSON.stringify(value ?? {}, null, 2)}</pre>
    </details>
  );
}

function formatAuditTime(value: unknown) {
  if (!value) return "—";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatAiCost(value?: number) {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  if (amount === 0) return "$0.00";
  const formatted = amount < 0.01 ? amount.toFixed(8).replace(/0+$/, "").replace(/\.$/, "") : amount.toFixed(2);
  return `$${formatted}`;
}

function formatAiCount(value?: number) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? new Intl.NumberFormat().format(amount) : "0";
}

function UsageStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-foreground/8 bg-foreground/[0.025] px-3 py-2.5">
      <p className="text-[8px] uppercase tracking-wider text-foreground/32">{label}</p>
      <p className="mt-1 truncate text-xs font-semibold">{value}</p>
    </div>
  );
}

function UsageDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[8px] uppercase tracking-wider text-foreground/28">{label}</p>
      <p className="mt-0.5 truncate font-medium text-foreground/68">{value}</p>
    </div>
  );
}

function ContextChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-foreground/8 bg-foreground/[0.025] px-2.5 py-2">
      <p className="text-[8px] uppercase tracking-wider text-foreground/32">{label}</p>
      <p className="mt-0.5 truncate text-[10px] font-semibold">{value}</p>
    </div>
  );
}
